import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Sparkles, Box, GitBranch, ArrowRight, Database, CheckCircle2, Upload, Users, CreditCard, BadgeCheck, Languages, ShieldCheck, Calculator } from "lucide-react";
import { Button } from "../components/ui/button";
import { WhyComparison } from "../components/WhyComparison";
import { seedDemoData, DEMO_ACCOUNTS } from "../services/seed";

const HERO = "https://static.prod-images.emergentagent.com/jobs/2a5d361b-6eeb-4d79-bf3e-cb78faa32db1/images/a23db4c302923219ac0b2b006822cbe10b081d5b482e5c115f1e72a073cb27fb.jpeg";

export default function Landing() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [seeding, setSeeding] = useState(false);

  // Demo/testing tools (seed button + demo credentials) are only exposed on the
  // preview/dev environment. In production (custom domain / emergent.host) they are
  // hidden so normal users cannot access data-loading tools or see test credentials.
  const isDemoEnv =
    typeof window !== "undefined" &&
    /(?:^|\.)preview\.emergentagent\.com$|^localhost$|^127\.0\.0\.1$/.test(window.location.hostname);

  const handleSeed = async () => {
    if (!isDemoEnv) return; // hard guard: no-op in production
    setSeeding(true);
    try {
      await seedDemoData();
      toast.success(t("demo.loaded"));
    } catch (e) {
      toast.error(e?.message || "Seed failed");
    } finally {
      setSeeding(false);
    }
  };

  const features = [
    { icon: Sparkles, title: t("landing.feature1Title"), desc: t("landing.feature1Desc") },
    { icon: Box, title: t("landing.feature2Title"), desc: t("landing.feature2Desc") },
    { icon: GitBranch, title: t("landing.feature3Title"), desc: t("landing.feature3Desc") },
  ];

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 py-16 sm:px-6 lg:grid-cols-2 lg:px-8 lg:py-24">
          <div className="animate-fade-up">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-brand/30 bg-brand/5 px-3 py-1 text-xs font-semibold text-brand">
              <Sparkles className="h-3.5 w-3.5" /> {t("landing.badge")}
            </span>
            <h1 className="mt-5 font-heading text-4xl font-bold tracking-tight text-foreground sm:text-5xl lg:text-6xl">
              {t("landing.title")}
            </h1>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-muted-foreground sm:text-lg">
              {t("landing.subtitle")}
            </p>
            <div className="mt-5 flex flex-wrap gap-2" data-testid="hero-audiences">
              {[t("roles.lab"), t("roles.designer"), "AI"].map((a) => (
                <span key={a} className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1 text-xs font-semibold text-foreground">
                  <CheckCircle2 className="h-3.5 w-3.5 text-brand" /> {a}
                </span>
              ))}
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Button
                size="lg"
                className="rounded-full bg-brand text-white hover:bg-brand/90"
                onClick={() => navigate("/auth")}
                data-testid="hero-get-started"
              >
                {t("landing.ctaPrimary")} <ArrowRight className="ml-1.5 h-4 w-4" />
              </Button>
              <Link to="/why">
                <Button size="lg" variant="ghost" className="rounded-full" data-testid="hero-why-link">
                  {t("site.whyLink")}
                </Button>
              </Link>
              {isDemoEnv && (
              <Button
                size="lg"
                variant="outline"
                className="rounded-full"
                onClick={handleSeed}
                disabled={seeding}
                data-testid="hero-seed-demo"
              >
                <Database className="mr-1.5 h-4 w-4" />
                {seeding ? t("demo.loading") : t("demo.loadData")}
              </Button>
            )}
            </div>

            {isDemoEnv && (
            <div className="mt-8 rounded-lg border border-border bg-secondary/40 p-4">
              <p className="mb-2 text-xs font-semibold uppercase tracking-[0.1em] text-muted-foreground">{t("demo.accounts")}</p>
              <div className="grid gap-1.5 text-sm sm:grid-cols-2">
                {DEMO_ACCOUNTS.map((a) => (
                  <div key={a.email} className="flex items-center gap-2">
                    <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                    <span className="font-mono text-xs">{a.email}</span>
                    <span className="text-xs text-muted-foreground">/ {a.password}</span>
                  </div>
                ))}
              </div>
            </div>
            )}
          </div>

          <div className="relative animate-fade-up" style={{ animationDelay: "120ms" }}>
            <div className="overflow-hidden rounded-2xl border border-border shadow-xl">
              <img src={HERO} alt="Dental lab" className="h-[420px] w-full object-cover" />
            </div>
            <div className="absolute -bottom-5 -left-5 hidden rounded-xl border border-border bg-background p-4 shadow-lg sm:block">
              <div className="flex items-center gap-2">
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-indigo-600 text-white">
                  <Sparkles className="h-4 w-4" />
                </div>
                <div>
                  <p className="text-xs font-semibold">AI Analysis</p>
                  <p className="font-mono text-[11px] text-muted-foreground">complexity: Medium</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="grid gap-6 md:grid-cols-3">
          {features.map((f, i) => (
            <div
              key={f.title}
              className="rounded-lg border border-border bg-card p-6 transition-transform duration-200 hover:-translate-y-0.5 animate-fade-up"
              style={{ animationDelay: `${i * 80}ms` }}
            >
              <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-lg bg-primary text-primary-foreground">
                <f.icon className="h-5 w-5" />
              </div>
              <h3 className="font-heading text-lg font-semibold tracking-tight">{f.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* For labs / designers */}
      <section className="border-t border-border bg-secondary/30">
        <div className="mx-auto grid max-w-7xl gap-8 px-4 py-16 sm:px-6 md:grid-cols-2 lg:px-8">
          {[
            { title: t("landing.forLabs"), points: [t("landing.labsPoint1"), t("landing.labsPoint2"), t("landing.labsPoint3")] },
            { title: t("landing.forDesigners"), points: [t("landing.designersPoint1"), t("landing.designersPoint2"), t("landing.designersPoint3")] },
          ].map((col) => (
            <div key={col.title} className="rounded-lg border border-border bg-card p-8">
              <h2 className="font-heading text-2xl font-semibold tracking-tight">{col.title}</h2>
              <ul className="mt-5 space-y-3">
                {col.points.map((p) => (
                  <li key={p} className="flex items-start gap-3 text-sm">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
                    <span className="text-muted-foreground">{p}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="border-t border-border" data-testid="section-how">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
          <div className="mb-10 text-center">
            <h2 className="font-heading text-3xl font-semibold tracking-tight">{t("site.howTitle")}</h2>
            <p className="mx-auto mt-3 max-w-2xl text-muted-foreground">{t("site.howSubtitle")}</p>
          </div>
          <div className="grid gap-6 md:grid-cols-4">
            {[
              { icon: Upload, k: "step1" },
              { icon: Users, k: "step2" },
              { icon: CreditCard, k: "step3" },
              { icon: BadgeCheck, k: "step4" },
            ].map((s, i) => (
              <div key={s.k} className="relative rounded-lg border border-border bg-card p-6">
                <span className="absolute right-4 top-4 font-heading text-2xl font-bold text-border">{i + 1}</span>
                <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-lg bg-primary text-primary-foreground">
                  <s.icon className="h-5 w-5" />
                </div>
                <h3 className="font-heading text-lg font-semibold tracking-tight">{t(`site.${s.k}Title`)}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{t(`site.${s.k}Desc`)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AI features */}
      <section className="border-t border-border bg-indigo-50/40" data-testid="section-ai">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
          <div className="mb-10 text-center">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-indigo-200 bg-white px-3 py-1 text-xs font-semibold text-indigo-700">
              <Sparkles className="h-3.5 w-3.5" /> {t("site.aiTitle")}
            </span>
            <h2 className="mt-4 font-heading text-3xl font-semibold tracking-tight">{t("site.aiSubtitle")}</h2>
          </div>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {(t("site.ai", { returnObjects: true }) || []).map((f, i) => {
              const Icon = [Sparkles, Languages, ShieldCheck, Calculator][i] || Sparkles;
              return (
                <div key={i} className="rounded-lg border border-indigo-200/70 bg-white p-6">
                  <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-lg bg-indigo-600 text-white">
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="font-heading text-base font-semibold tracking-tight">{f.t}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{f.d}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why teaser */}
      <section className="border-t border-border" data-testid="section-why-teaser">
        <div className="mx-auto max-w-5xl px-4 py-16 sm:px-6 lg:px-8">
          <div className="mb-8 text-center">
            <h2 className="font-heading text-3xl font-semibold tracking-tight">{t("why.title")}</h2>
            <p className="mx-auto mt-3 max-w-2xl text-muted-foreground">{t("why.subtitle")}</p>
          </div>
          <WhyComparison />
          <div className="mt-6 text-center">
            <Link to="/why" className="text-sm font-semibold text-brand hover:underline" data-testid="why-more-link">
              {t("site.whyLink")} <ArrowRight className="inline h-3.5 w-3.5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="border-t border-border bg-secondary/30" data-testid="section-pricing">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
          <div className="mb-10 text-center">
            <h2 className="font-heading text-3xl font-semibold tracking-tight">{t("site.pricingTitle")}</h2>
            <p className="mx-auto mt-3 max-w-2xl text-muted-foreground">{t("site.pricingSubtitle")}</p>
            <div className="mx-auto mt-5 max-w-2xl rounded-xl border border-brand/30 bg-brand/5 px-5 py-4" data-testid="launch-offer-banner">
              <p className="flex items-center justify-center gap-1.5 font-heading text-base font-bold text-brand">
                <Sparkles className="h-4 w-4" /> {t("promo.landingTitle")}
              </p>
              <p className="mt-1 text-sm text-muted-foreground">{t("promo.landingBody")}</p>
            </div>
          </div>
          <div className="grid gap-6 md:grid-cols-3">
            <div className="relative rounded-xl border-2 border-brand bg-card p-8 shadow-sm" data-testid="plan-free">
              <span className="absolute -top-3 left-6 rounded-full bg-brand px-3 py-0.5 text-[11px] font-bold text-white">MVP</span>
              <h3 className="font-heading text-xl font-semibold">{t("site.planFreeName")}</h3>
              <p className="mt-2 font-heading text-3xl font-bold">{t("site.planFreePrice")}</p>
              <p className="text-sm text-muted-foreground">{t("site.planFreeDesc")}</p>
              <ul className="mt-5 space-y-2.5 text-sm">
                {["planFree1", "planFree2", "planFree3"].map((k) => (
                  <li key={k} className="flex items-start gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-brand" /><span className="text-muted-foreground">{t(`site.${k}`)}</span></li>
                ))}
              </ul>
              <Button className="mt-6 w-full rounded-full bg-brand text-white hover:bg-brand/90" onClick={() => navigate("/auth")} data-testid="plan-free-cta">{t("site.ctaButton")}</Button>
            </div>
            {[{ n: "planProName", d: "planProDesc" }, { n: "planEntName", d: "planEntDesc" }].map((p) => (
              <div key={p.n} className="rounded-xl border border-border bg-card/60 p-8" data-testid={`plan-${p.n}`}>
                <span className="rounded-full bg-secondary px-3 py-0.5 text-[11px] font-semibold text-muted-foreground">{t("site.planSoon")}</span>
                <h3 className="mt-3 font-heading text-xl font-semibold">{t(`site.${p.n}`)}</h3>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{t(`site.${p.d}`)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-border" data-testid="section-faq">
        <div className="mx-auto max-w-3xl px-4 py-16 sm:px-6 lg:px-8">
          <h2 className="mb-8 text-center font-heading text-3xl font-semibold tracking-tight">{t("site.faqTitle")}</h2>
          <div className="space-y-4">
            {(t("site.faq", { returnObjects: true }) || []).map((f, i) => (
              <div key={i} className="rounded-lg border border-border bg-card p-5" data-testid={`faq-${i}`}>
                <h3 className="font-heading text-base font-semibold">{f.q}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{f.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="border-t border-border bg-primary text-primary-foreground" data-testid="section-cta">
        <div className="mx-auto max-w-4xl px-4 py-16 text-center sm:px-6 lg:px-8">
          <h2 className="font-heading text-3xl font-bold tracking-tight sm:text-4xl">{t("site.ctaTitle")}</h2>
          <p className="mx-auto mt-4 max-w-xl text-primary-foreground/80">{t("site.ctaSubtitle")}</p>
          <Button size="lg" className="mt-8 rounded-full bg-white text-primary hover:bg-white/90" onClick={() => navigate("/auth")} data-testid="cta-final-button">
            {t("site.ctaButton")} <ArrowRight className="ml-1.5 h-4 w-4" />
          </Button>
        </div>
      </section>

      <footer className="border-t border-border py-8 text-center text-sm text-muted-foreground">
        DentalCAD Connect · {new Date().getFullYear()}
      </footer>
    </div>
  );
}
